import { Component, Input, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { AlertsService } from '../../../alerts/services/alerts.service';
import { DetailContainerComponent } from '../../../common/components/detail-container/detail-container.component';
import { consts } from '../../../common/modals/consts';
import { DocumentModalService } from '../../../documents/services/document-modal.service';
import { DocumentService } from '../../../documents/services/document.service';
import { Observation, ObservationType, ObservationTypeString } from '../../../domain-model/observation.class';
import { IBreadcrumb } from '../../../eca-breadcrumb/models/breadcrumb.interface';
import { EcaBreadcrumbService } from '../../../eca-breadcrumb/services/eca-breadcrumb.service';
import { HelpConsts } from '../../../help/models/help-consts';
import { HelpModalService } from '../../../help/services/help-modal.service';
import { DocumentEntityType } from '../../../message-models/document-entity-type.enum';
import { IEntityDocumentSummary } from '../../../message-models/entity-document-summary';
import { IObservationSummary } from '../../../message-models/observation-summary.interface';
import { IObservation } from '../../../message-models/observation.interface';
import { ErrorHandlingService } from '../../../services/error-handling.service';
import { EcaConfirmDeleteModalOptions } from '../../../shared/models/confirm-delete-modal-options.class';
import { EcaConfirmProcessService } from '../../../shared/services/eca-confirm-process.service';
import { IUserInfo } from '../../../users/models/userInfo.interface';
import { OurUserService } from '../../../users/services/user.service';
import { ObservationDocumentService } from '../../services/observation-document.service';
import { ObservationsService } from '../../services/observations.service';

@Component({
  selector: 'app-observation-details',
  templateUrl: './observation-details.component.html',
  styleUrls: ['./observation-details.component.scss']
})

export class ObservationDetailsComponent {

  @ViewChild('observationDetailsContainerComponent') observationDetailsContainerComponent: DetailContainerComponent;
  private loggedInUser: IUserInfo;
  loggedInUserSubscription: Subscription;

  observationSummary: IObservationSummary;
  observationTitle: string;
  observation: Observation = new Observation({} as IObservation);
  tempObservation: Observation = new Observation({} as IObservation);
  isObservationLoading: boolean = true;
  observationId: string

  observationDocuments: IEntityDocumentSummary[] = [];

  companyId: string = consts.emptyGuid;
  isEditing: boolean = false;
  saveAttempted: boolean = false;

  constructor(
    private userService: OurUserService,
    private ecaBreadcrumbService: EcaBreadcrumbService,
    private helpService: HelpModalService,
    private errorHandlingService: ErrorHandlingService,
    private activatedRoute: ActivatedRoute,
    private observationsService: ObservationsService,
    private router: Router,
    private documentService: DocumentService,
    private documentModalService: DocumentModalService,
    private observationDocumentService: ObservationDocumentService,
    private confirmProcessService: EcaConfirmProcessService,
    private alertsService: AlertsService,
  ) {
    this.observationId = this.activatedRoute.snapshot.params.observationId
  };

  ngOnInit(): void {
    this.userService.getLoggedInUser().subscribe(user => {
      this.loggedInUserSubscription = this.userService.getLoggedInUserSubject().subscribe(user => {
        this.loggedInUser = user;
        this.companyId = this.loggedInUser.companyId;

        //Get Observation Summary
        this.observationsService.getObservationSummaryById(this.observationId).subscribe(result => {
          this.observationSummary = result;
          this.observationSummary.observationTypeString = ObservationTypeString[this.observationSummary.observationType];
          this.observationTitle = this.observationSummary.observationTypeString

          this.populateBreadcrumbs();
        }, () => {
          this.errorHandlingService.showErrorWarning().subscribe(() => {
          });
        });

        //Get Observation
        this.observationsService.getObservationById(this.observationId).subscribe(result => {
          this.observation = new Observation(result);
          this.tempObservation = new Observation(result);
        }, () => {
          this.errorHandlingService.showErrorWarning().subscribe(() => {
          });
        });

        //Get Observation Documents
        this.observationDocumentService.getAllDocumentSummariesForObservation(this.companyId, this.observationId, false).subscribe(result => {
          this.observationDocuments = result;
        }, () => {
          this.errorHandlingService.showErrorWarning().subscribe(() => {
            error => {
              console.log(error);
            }
          });
        });
      });
      this.isObservationLoading = false;
    });
  };

  private populateBreadcrumbs() {
    const operativeBreadCrumbs = new Array<IBreadcrumb>();
    switch (this.observationSummary.observationType) {
      case ObservationType.Operative:        
      case ObservationType.TrainingRecord: {
        operativeBreadCrumbs.push({ label: 'Operatives', iconName: 'users', routeLink: '/operatives' } as IBreadcrumb);
        operativeBreadCrumbs.push({ label: `${this.observationSummary.operativeName}`, iconName: 'user', routeLink: `/operative/${this.observationSummary.operativeId}` } as IBreadcrumb);
        operativeBreadCrumbs.push({ label: this.observationSummary.observationTypeString + '- Observation', iconName: 'eye' } as IBreadcrumb);
        break;
      }
      case ObservationType.Company: {
        
      }
      case ObservationType.CompanyDocument:
      case ObservationType.OperativeDocument:
      case ObservationType.TrainingRecordDocument: {
        operativeBreadCrumbs.push({ label: this.loggedInUser?.companyName.toString() + ' - ' + 'Enterprise Documents', iconName: 'books', routeLink: `/company-documents/` } as IBreadcrumb);
        operativeBreadCrumbs.push({ label: this.observationSummary.observationTypeString + '- Observation', iconName: 'eye' } as IBreadcrumb);
        break;
      }
    }
    this.ecaBreadcrumbService.breadCrumbs.next(operativeBreadCrumbs);
  }

  openObservationDetailsHelp() {
    this.helpService.openHelpModal(HelpConsts.CompanyTrainingCoursesHelp);
  }

  editDetails() {
    if (this.observationDetailsContainerComponent.isCollapsed) {
      this.observationDetailsContainerComponent.panelToggle();
    }
    this.isEditing = true;
  }

  saveDetails() {
    this.isEditing = false;
    if (this.tempObservation.isValid) {
      this.isObservationLoading = true;

      switch (this.tempObservation.observationType) {
        case ObservationType.Operative: {
          this.observationsService.createOrUpdateOperativeObservation(
            this.tempObservation,
            this.observationSummary.operativeId,
            this.observationSummary.companyId,
            this.observationSummary.operativeObservationId).subscribe(operativeObservation => {
              this.alertsService.getAlertsCountForCompany(this.loggedInUser?.companyId).subscribe();
              this.isObservationLoading = false;
            }, () => {
              this.errorHandlingService.showErrorWarning().subscribe(() => {
              });
            });
          break;
        }
        case ObservationType.TrainingRecord: {
          this.observationsService.createOrUpdateTrainingRecordObservation(
            this.tempObservation,
            this.observationSummary.trainingRecordId,
            this.observationSummary.companyId,
            this.observationSummary.trainingRecordObservationId).subscribe(traingingRecordObservation => {
              this.isObservationLoading = false;
              this.alertsService.getAlertsCountForCompany(this.loggedInUser?.companyId).subscribe();
            }, () => {
              this.errorHandlingService.showErrorWarning().subscribe(() => {
              });
            });
          break;
        }
        case ObservationType.Company: {
          this.observationsService.createOrUpdateCompanyObservation(
            this.tempObservation,
            this.observationSummary.companyId,
            this.observationSummary.companyObservationId).subscribe(companyObservation => {
              this.isObservationLoading = false;
              this.alertsService.getAlertsCountForCompany(this.loggedInUser?.companyId).subscribe();
            }, () => {
              this.errorHandlingService.showErrorWarning().subscribe(() => {
              });
            });
          break;
        }
        case ObservationType.CompanyDocument:
        case ObservationType.OperativeDocument:
        case ObservationType.TrainingRecordDocument: {
          this.observationsService.createOrUpdateDocumentObservation(
            this.tempObservation,
            this.observationSummary.documentId,
            this.observationSummary.companyId,
            this.observationSummary.documentObservationId).subscribe(documentObservation => {
              this.isObservationLoading = false;
              this.alertsService.getAlertsCountForCompany(this.loggedInUser?.companyId).subscribe();
            }, () => {
              this.errorHandlingService.showErrorWarning().subscribe(() => {
              });
            });
          break;
        }
      }
    }
  }

  cancelEdit() {
    this.saveAttempted = false;
    this.tempObservation = new Observation(this.observation);
    this.isEditing = false;
  }

  ngOnDestroy() {
    this.loggedInUserSubscription.unsubscribe();
  }

  //Observation Documents
  addObservationDocument() {
    this.documentModalService.openDocumentUploaderModal(null, DocumentEntityType.observation, 'Add Document')
      .subscribe(documentModalRef => {
        documentModalRef.content.documentUploadSave.subscribe((documentUploadRequest: FormData) => {
          documentModalRef.dynamicDialogRef.close();
          documentUploadRequest.append('ObservationId', this.observationSummary.observationId);
          documentUploadRequest.append('CompanyId', this.companyId);

          this.observationDocumentService
            .createOrUpdateObservationDocument(documentUploadRequest).subscribe(observationSummary => {
              this.observationDocuments.unshift(observationSummary);
            }, () => {
              this.errorHandlingService.showErrorWarning().subscribe(() => {
              });
            });
        });
      });
  }

  viewObservationDocument(entityDocumentSummary: IEntityDocumentSummary) {
    this.router.navigate(['observation-document', entityDocumentSummary.documentId]);
  }

  downloadObservationDocument(entityDocumentSummary: IEntityDocumentSummary) {
    this.documentService.downloadOrOpenDocument(entityDocumentSummary, true);
  }

  openObservationDocument(entityDocumentSummary: IEntityDocumentSummary) {
    this.documentService.downloadOrOpenDocument(entityDocumentSummary);
  }

  deleteObservationDocumentClick(entityDocumentSummary: IEntityDocumentSummary) {
    this.confirmProcessService.openConfirmProcessModal('Are you sure you want to remove this document?',
      'Remove ' + entityDocumentSummary.originalFilename, new EcaConfirmDeleteModalOptions()).subscribe({
        next: response => {
          if (response) {
            this.deleteObservationDocument(entityDocumentSummary);
          }
        }
      });
  }

  deleteObservationDocument(entityDocumentSummary: IEntityDocumentSummary) {
    this.observationDocumentService.deleteObservationDocument(entityDocumentSummary.documentId, this.companyId).subscribe(
      result => {
        if (result) {
          const documentIndex = this.observationDocuments.findIndex(c => c.documentId !== entityDocumentSummary.documentId);
          this.observationDocuments.splice(documentIndex, 1);
          this.alertsService.getAlertsCountForCompany(this.loggedInUser?.companyId).subscribe();
        }
      }, () => {
        this.errorHandlingService.showErrorWarning().subscribe(() => {
        });
      });
  }
}
